// src/components/FarmMark/FarmMarkMyImpact.js

/**
 * My Impact tab. personal dashboard with points breakdown,
 * CO2 ring, and recent activity timeline.
 */

import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Share2 } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { getUserImpact } from "../../services/farmMarkService";
import { co2Equivalents } from "../../utils/co2Calculator";
import { usePoints } from "../../contexts/PointsContext";
import PointsBadge from "../PointsBadge";
import NoData from "../NoData";
import LoadingSpinner from "../ui/LoadingSpinner";
import { IMPACT_TYPES } from "../../constants/farmMarkConstants";
import { errorDev } from '../../utils/logger.js';

function BreakdownRow({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-neutral-800 last:border-0">
      <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
      <span className="text-sm font-bold text-green-700 dark:text-green-400">{value}</span>
    </div>
  );
}

function StatTile({ title, value, subtitle }) {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-4">
      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wide">{title}</div>
      <div className="text-2xl font-extrabold text-gray-900 dark:text-gray-100">{value}</div>
      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</div>
    </div>
  );
}

function ActionEntry({ entry, t }) {
  const ts = entry.timestamp?.seconds
    ? new Date(entry.timestamp.seconds * 1000)
    : null;
  const timeText = ts ? ts.toLocaleDateString() : "";

  const typeConfig = {
    [IMPACT_TYPES.PRODUCT_LISTING]: {
      label: t("myImpact.actionListing"),
      badge: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
    },
    [IMPACT_TYPES.PRICE_CONTRIBUTION]: {
      label: t("myImpact.actionPriceReport"),
      badge: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
    },
    [IMPACT_TYPES.RATING]: {
      label: t("myImpact.actionRating"),
      badge: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
    },
    [IMPACT_TYPES.PLANTING]: {
      label: t("myImpact.actionPlanting"),
      badge: "bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300",
    },
  };

  const config = typeConfig[entry.type] || {
    label: entry.type,
    badge: "bg-gray-100 text-gray-700 dark:bg-neutral-800 dark:text-gray-300",
  };

  return (
    <div className="flex items-center gap-3 p-2.5 rounded-lg border border-gray-100 dark:border-neutral-800 bg-white dark:bg-neutral-900">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
          {config.label}
        </div>
        <div className="text-xs text-gray-400 dark:text-gray-500">{timeText}</div>
      </div>
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] whitespace-nowrap ${config.badge}`}>
        +{entry.points || 0} {t("leaderboard.ip")}
      </span>
    </div>
  );
}

export default function FarmMarkMyImpact() {
  const { t } = useTranslation("farmMark");
  const { points } = usePoints();
  const { user: currentUser } = useAuth();
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    getUserImpact(currentUser.uid).then((result) => {
      if (!cancelled) {
        setImpact(result);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [currentUser, points]);

  const equiv = useMemo(() => {
    return co2Equivalents(impact?.co2Saved || 0);
  }, [impact]);

  const totalPoints = impact?.totalIP ?? points ?? 0;
  const recentActivityCount = impact?.recentActions?.length || 0;

  const handleShare = async () => {
    const text = t("myImpact.shareText", {
      points: points || 0,
      co2: impact?.co2Saved || 0,
    });
    const url = window.location.origin + "/farmmark";

    if (navigator.share) {
      try {
        await navigator.share({ title: t("myImpact.shareTitle"), text, url });
        return;
      } catch (err) { errorDev("Operation failed:", err);  /* fallback */ }
    }
    try {
      await navigator.clipboard.writeText(`${text}\n${url}`);
    } catch (err) { errorDev("Operation failed:", err);  /* silent */ }
  };

  if (!currentUser) {
    return (
      <div className="p-6 rounded-xl border border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-center">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">
          {t("myImpact.signInTitle")}
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {t("myImpact.signInDesc")}
        </p>
        <div className="flex items-center justify-center gap-3">
          <Link
            to="/login"
            className="px-4 py-2 bg-green-700 hover:bg-green-800 text-white font-semibold rounded-xl text-sm transition-colors"
          >
            {t("myImpact.signIn")}
          </Link>
          <Link
            to="/signup"
            className="px-4 py-2 border border-gray-200 dark:border-neutral-700 rounded-xl text-sm font-medium hover:shadow-sm transition-shadow"
          >
            {t("myImpact.createAccount")}
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="py-12">
        <LoadingSpinner size="medium" />
      </div>
    );
  }

  if (!impact) {
    return (
      <NoData
        title={t("myImpact.noActions")}
        description={t("myImpact.noActionsDesc")}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 rounded-full overflow-hidden bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
            {currentUser.photoURL ? (
              <img src={currentUser.photoURL} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-lg font-bold text-green-700 dark:text-green-300">
                {(currentUser.displayName || currentUser.email || "?").charAt(0).toUpperCase()}
              </span>
            )}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-2xl font-extrabold text-gray-900 dark:text-gray-100">
                  {totalPoints.toLocaleString()}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">{t("leaderboard.ip")}</span>
              </div>
              <PointsBadge points={totalPoints} size="sm" />
            </div>
          </div>
          <button
            onClick={handleShare}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-800 text-gray-600 dark:text-gray-300 transition"
            aria-label={t("myImpact.share")}
          >
            <Share2 className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatTile
          title={t("myImpact.totalIP", { defaultValue: "Total Impact" })}
          value={`${totalPoints.toLocaleString()} ${t("leaderboard.ip")}`}
          subtitle={t("myImpact.title", { defaultValue: "My Impact" })}
        />
        <StatTile
          title={t("myImpact.co2Saved")}
          value={`${(impact?.co2Saved || 0).toLocaleString()} ${t("co2.kgShort")}`}
          subtitle={`${(equiv?.treesPlanted || 0).toLocaleString()} ${t("co2.treesPlanted")}`}
        />
        <StatTile
          title={t("myImpact.fromRatings")}
          value={`${(impact?.fromRatings || 0).toLocaleString()} ${t("leaderboard.ip")}`}
          subtitle={t("myImpact.actionRating", { defaultValue: "You received a rating" })}
        />
        <StatTile
          title={t("myImpact.recentActions")}
          value={recentActivityCount.toLocaleString()}
          subtitle={t("leaderboard.actions", { defaultValue: "actions" })}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2 rounded-xl border border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {t("myImpact.breakdown")}
          </h3>
          <BreakdownRow label={t("myImpact.fromSelling")} value={`${impact.fromSelling} ${t("leaderboard.ip")}`} />
          <BreakdownRow label={t("myImpact.fromPriceReports")} value={`${impact.fromPriceReports} ${t("leaderboard.ip")}`} />
          <BreakdownRow label={t("myImpact.fromFixers", { defaultValue: "From Fixers" })} value={`${impact.fromFixers || 0} ${t("leaderboard.ip")}`} />
          <BreakdownRow label={t("myImpact.fromRatings")} value={`${impact.fromRatings} ${t("leaderboard.ip")}`} />
          {impact.fromOther > 0 && (
            <BreakdownRow label={t("myImpact.fromOther")} value={`${impact.fromOther} ${t("leaderboard.ip")}`} />
          )}
        </div>

        <div className="rounded-xl border border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
            {t("co2.equivalent")}
          </h3>
          <div className="space-y-2.5">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">{t("co2.treesPlanted")}</span>
              <span className="font-semibold text-gray-900 dark:text-gray-100">{equiv.treesPlanted}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">{t("co2.carKmAvoided")}</span>
              <span className="font-semibold text-gray-900 dark:text-gray-100">{equiv.carKmAvoided.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">{t("co2.mealsRescued")}</span>
              <span className="font-semibold text-gray-900 dark:text-gray-100">{equiv.mealsRescued.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-900">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-neutral-800">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {t("myImpact.recentActions")}
          </h3>
        </div>
        <div className="max-h-[400px] overflow-y-auto p-3 space-y-2">
          {impact.recentActions.length === 0 ? (
            <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
              {t("myImpact.noActionsDesc")}
            </div>
          ) : (
            impact.recentActions.map((entry) => (
              <ActionEntry key={entry.id} entry={entry} t={t} />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
